// src/services/MeetingCostService.js

import { BaseCostService, DEFAULT_COST_CONFIG } from './BaseCostService';

const SNARKY_QUIPS = [
  { threshold: 500, quip: "A modest investment in collective confusion." },
  { threshold: 1500, quip: "Roughly the price of a well-catered lunch — without the food or joy." },
  { threshold: 3000, quip: "Not bad for 90 minutes of polite disagreement." },
  { threshold: 6000, quip: "Equivalent to a decent team-building day. With actual outcomes." },
  { threshold: 10000, quip: "You could've upgraded everyone's monitors instead." },
  { threshold: 20000, quip: "That's an entire quarter's worth of Jira tickets no one will finish." },
  { threshold: Infinity, quip: "Congratulations: this meeting now has its own line item in Finance." },
];

const RECURRENCE_MULTIPLIERS = {
  none: 1,
  daily: 260, // 5 days/week * 52 weeks
  weekly: 52,
  fortnightly: 26,
  monthly: 12,
  quarterly: 4,
};

export class MeetingCostService extends BaseCostService {
  constructor(config = DEFAULT_COST_CONFIG) {
    super(config);
  }

  calculateCost({ attendees, durationMinutes, prepTimeMinutes }) {
    this.validateInput({ attendees, durationMinutes, prepTimeMinutes });

    let total = 0;

    attendees.forEach(({ count, hourlyRate }) => {
      const totalHours = this.calculateTotalHours(durationMinutes, prepTimeMinutes);
      const trueCostPerHour = this.calculateTrueCostPerHour(hourlyRate);
      total += count * trueCostPerHour * totalHours;
    });

    const roundedCost = Math.round(total);

    return {
      totalCost: roundedCost,
      snarkyQuip: this.getSnarkyQuip(roundedCost),
    };
  }

  validateInput({ attendees, durationMinutes, prepTimeMinutes }) {
    if (!Array.isArray(attendees)) {
      throw new Error('Attendees must be an array');
    }

    if (typeof durationMinutes !== 'number' || durationMinutes < 0) {
      throw new Error('Duration minutes must be a non-negative number');
    }

    if (typeof prepTimeMinutes !== 'number' || prepTimeMinutes < 0) {
      throw new Error('Prep time minutes must be a non-negative number');
    }

    attendees.forEach((attendee, index) => {
      if (typeof attendee.count !== 'number' || attendee.count < 0) {
        throw new Error(`Attendee ${index} count must be a non-negative number`);
      }

      if (typeof attendee.hourlyRate !== 'number' || attendee.hourlyRate < 0) {
        throw new Error(`Attendee ${index} hourly rate must be a non-negative number`);
      }
    });
  }

  calculateTotalHours(durationMinutes, prepTimeMinutes) {
    const durationHours = durationMinutes / 60;
    const prepHours = prepTimeMinutes / 60;
    return durationHours + prepHours;
  }


  getSnarkyQuip(cost) {
    const quip = SNARKY_QUIPS.find(({ threshold }) => cost < threshold);
    return quip ? quip.quip : SNARKY_QUIPS[SNARKY_QUIPS.length - 1].quip;
  }

  calculateRecurringCost({ attendees, durationMinutes, prepTimeMinutes, recurrence }) {
    // Validate recurrence frequency
    if (!recurrence || !(recurrence in RECURRENCE_MULTIPLIERS)) {
      throw new Error(`Invalid recurrence frequency: ${recurrence}`);
    }

    // Calculate single meeting cost using existing method
    const singleMeetingResult = this.calculateCost({ attendees, durationMinutes, prepTimeMinutes });
    const singleMeetingCost = singleMeetingResult.totalCost;

    // Calculate recurring details
    const occurrencesPerYear = RECURRENCE_MULTIPLIERS[recurrence];
    const yearlyTotalCost = singleMeetingCost * occurrencesPerYear;

    // Generate yearly snarky quip for the total cost
    const yearlySnarkyQuip = this.getSnarkyQuip(yearlyTotalCost);

    return {
      singleMeetingCost,
      yearlyTotalCost,
      recurrenceDetails: {
        frequency: recurrence,
        occurrencesPerYear,
      },
      yearlySnarkyQuip,
      snarkyQuip: singleMeetingResult.snarkyQuip, // Include original single meeting quip
    };
  }

}

// Export default instance for backward compatibility
export const meetingCostService = new MeetingCostService();